/**
 * EmptyState — shown when a tab or section has no data yet.
 *
 * Provides a clear call-to-action so users know how to populate the view.
 * Used in example environment tabs to guide first-time users (ADR-0015 D5).
 */
import { type FC, type ReactNode } from 'react'
import { type LucideIcon, Info } from 'lucide-react'

interface EmptyStateProps {
  /** Icon to display above the message. */
  icon?: LucideIcon
  /** Primary heading. */
  title: string
  /** Descriptive text explaining what this area is for. */
  description: string
  /** Optional action button or link. */
  action?: ReactNode
  /** Status label: [Example], [Experimental], or [Production]. */
  statusLabel?: 'Example' | 'Experimental' | 'Production'
}

/** Empty state placeholder for data-less views. */
export const EmptyState: FC<EmptyStateProps> = ({
  icon: Icon = Info,
  title,
  description,
  action,
  statusLabel,
}) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="rounded-full bg-surface-secondary p-4 mb-4">
      <Icon className="h-8 w-8 text-content-tertiary" />
    </div>
    <h3 className="text-lg font-medium text-content mb-1">{title}</h3>
    <p className="text-sm text-content-tertiary max-w-md mb-4">{description}</p>
    {statusLabel && (
      <span
        className={`text-xs font-medium px-2 py-0.5 rounded mb-3 ${
          statusLabel === 'Production'
            ? 'bg-green-100 text-green-700'
            : statusLabel === 'Experimental'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-blue-100 text-blue-700'
        }`}
      >
        {statusLabel}
      </span>
    )}
    {action && <div className="mt-2">{action}</div>}
  </div>
)
